/**
 * Encrypted localStorage utility using Web Crypto API
 * Provides secure storage for sensitive cache data
 */

// Encryption configuration
const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12; // 96 bits for GCM

interface EncryptedData {
  encrypted: string; // Base64 encoded encrypted data
  iv: string; // Base64 encoded initialization vector
  timestamp: number; // When the data was encrypted
}

/**
 * Generate a consistent encryption key based on user/session data
 * This ensures the same key across browser sessions for the same user
 */
const generateEncryptionKey = async (): Promise<CryptoKey> => {
  // Use a combination of user identifier and a consistent string
  // Made more robust by including a fallback identifier
  const hostname = window.location.hostname || 'localhost';
  const keyMaterial = 'mapDataCache-' + hostname + '-v2'; // Added version for consistency
  
  // Convert string to ArrayBuffer
  const encoder = new TextEncoder();
  const keyData = encoder.encode(keyMaterial);
  
  // Create a key from the material
  const baseKey = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );
  
  // Derive actual encryption key using PBKDF2
  const salt = encoder.encode('ethica-map-cache-salt-v2'); // Updated salt version
  
  return await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: 10000,
      hash: 'SHA-256'
    },
    baseKey,
    {
      name: ALGORITHM,
      length: KEY_LENGTH
    },
    false,
    ['encrypt', 'decrypt']
  );
};

/**
 * Encrypt data and store in localStorage
 */
export const setEncryptedItem = async (key: string, data: any): Promise<void> => {
  try {
    // Check if Web Crypto API is available
    if (!crypto.subtle) {
      console.warn('EncryptedStorage: Web Crypto API not available, falling back to plain storage');
      localStorage.setItem(key, JSON.stringify(data));
      return;
    }

    // Serialize data
    const serializedData = JSON.stringify(data);
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(serializedData);
    
    // Generate encryption key
    const encryptionKey = await generateEncryptionKey();
    
    // Generate random IV
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    
    // Encrypt data
    const encryptedBuffer = await crypto.subtle.encrypt(
      {
        name: ALGORITHM,
        iv: iv
      },
      encryptionKey,
      dataBuffer
    );
    
    // Prepare encrypted data structure
    const encryptedData: EncryptedData = {
      encrypted: arrayBufferToBase64(encryptedBuffer),
      iv: arrayBufferToBase64(iv),
      timestamp: Date.now()
    };
    
    // Store encrypted data
    localStorage.setItem(key, JSON.stringify(encryptedData));
    
    if (process.env.NODE_ENV === 'development') {
      console.log('EncryptedStorage: Data encrypted and stored securely');
    }
  } catch (error) {
    console.error('EncryptedStorage: Encryption failed, falling back to plain storage', error);
    localStorage.setItem(key, JSON.stringify(data));
  }
};

/**
 * Retrieve and decrypt data from localStorage
 */
export const getEncryptedItem = async (key: string): Promise<any | null> => {
  try {
    const storedData = localStorage.getItem(key);
    if (!storedData) {
      return null;
    }

    // Check if Web Crypto API is available
    if (!crypto.subtle) {
      console.warn('EncryptedStorage: Web Crypto API not available');
      try {
        return JSON.parse(storedData);
      } catch (parseError) {
        console.error('EncryptedStorage: Failed to parse plain JSON data', parseError);
        // Clear corrupted data
        localStorage.removeItem(key);
        return null;
      }
    }

    let encryptedData: EncryptedData;
    try {
      encryptedData = JSON.parse(storedData);
    } catch (parseError) {
      // Might be plain text data from before encryption was implemented
      console.log('EncryptedStorage: Found legacy plain text data, attempting to parse');
      try {
        return JSON.parse(storedData);
      } catch (legacyParseError) {
        console.error('EncryptedStorage: Failed to parse legacy data', legacyParseError);
        // Clear corrupted data
        localStorage.removeItem(key);
        return null;
      }
    }

    // Check if this looks like encrypted data
    if (!encryptedData || typeof encryptedData !== 'object' || !encryptedData.encrypted || !encryptedData.iv) {
      // Probably plain text data or malformed encrypted data
      if (typeof encryptedData === 'object' && encryptedData !== null) {
        return encryptedData;
      }
      // If it's not a valid object, clear it
      localStorage.removeItem(key);
      return null;
    }
    
    // Generate encryption key (same as used for encryption)
    const encryptionKey = await generateEncryptionKey();
    
    // Convert base64 back to ArrayBuffer
    let encryptedBuffer: ArrayBuffer;
    let iv: ArrayBuffer;
    
    try {
      encryptedBuffer = base64ToArrayBuffer(encryptedData.encrypted);
      iv = base64ToArrayBuffer(encryptedData.iv);
    } catch (conversionError) {
      console.error('EncryptedStorage: Failed to convert base64 data', conversionError);
      // Clear corrupted data
      localStorage.removeItem(key);
      return null;
    }
    
    // Decrypt data
    try {
      const decryptedBuffer = await crypto.subtle.decrypt(
        {
          name: ALGORITHM,
          iv: iv
        },
        encryptionKey,
        encryptedBuffer
      );
      
      // Convert back to string and parse JSON
      const decoder = new TextDecoder();
      const decryptedString = decoder.decode(decryptedBuffer);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('EncryptedStorage: Data decrypted successfully');
      }
      
      return JSON.parse(decryptedString);
    } catch (decryptError) {
      console.error('EncryptedStorage: Decryption failed - possibly wrong key or corrupted data', decryptError);
      // Clear corrupted encrypted data
      localStorage.removeItem(key);
      return null;
    }
  } catch (error) {
    console.error('EncryptedStorage: Unexpected error during decryption', error);
    
    // Last resort: try to parse as plain JSON in case it's legacy data
    try {
      const storedData = localStorage.getItem(key);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        console.warn('EncryptedStorage: Fallback to plain JSON parsing successful');
        return parsedData;
      }
    } catch (fallbackError) {
      console.error('EncryptedStorage: Fallback parsing also failed', fallbackError);
      // Data is corrupted, remove it
      localStorage.removeItem(key);
    }
    
    return null;
  }
};

/**
 * Remove encrypted item from localStorage
 */
export const removeEncryptedItem = (key: string): void => {
  localStorage.removeItem(key);
  if (process.env.NODE_ENV === 'development') {
    console.log('EncryptedStorage: Encrypted data removed');
  }
};

/**
 * Utility functions for base64 conversion
 */
const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

/**
 * Check if encrypted storage is available
 */
export const isEncryptedStorageAvailable = (): boolean => {
  return typeof crypto !== 'undefined' && !!crypto.subtle;
};

export default {
  setItem: setEncryptedItem,
  getItem: getEncryptedItem,
  removeItem: removeEncryptedItem,
  isAvailable: isEncryptedStorageAvailable
}; 